"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { maxComboOptionInstruments } from "#/config/combo";
import { routes } from "#/config/routes";
import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";

import { useMarket } from "../app/portfolio/market";
import { useShowTradePanel } from "../app/tradePanel/showTradePanel";
import { useTradePanelTab } from "../app/tradePanel/tradePanelTab";
import { useComboActiveInstrumentId } from "./comboActiveInstrumentId";
import { useComboInstruments } from "./comboInstruments";
import { useComboState } from "./comboState";

export const ComboUpdater = () => {
  const pathname = usePathname();
  const { market } = useMarket();
  const { isCombo, setTradePanelTab } = useTradePanelTab();
  const { setComboState } = useComboState();
  const { setShowTradePanel } = useShowTradePanel();
  const { comboInstruments } = useComboInstruments();
  const { comboOptionInstruments, handleDeleteAll } =
    useComboInstrumentsController();
  const { comboActiveInstrumentId, setComboActiveInstrumentId } =
    useComboActiveInstrumentId();

  const isOnComboPage = pathname === routes.combo;
  const isOnOptionsPage = pathname === routes.options();

  useEffect(() => {
    // Behavior specific to trading single combo orders:
    if (isOnOptionsPage && comboInstruments.length && isCombo) {
      setTimeout(() => {
        setShowTradePanel(true);
      }, 175);
    }
  }, [isCombo, isOnOptionsPage, comboInstruments.length, setShowTradePanel]);

  useEffect(() => {
    // Close panel when navigating to combo page and set trade panel tab to combo:
    if (isOnComboPage) {
      setTradePanelTab("combo");
      setTimeout(() => {
        setShowTradePanel(false);
      }, 350);
    }
  }, [isOnComboPage, setShowTradePanel, setTradePanelTab]);

  // Reset combo option instruments when market changes
  useEffect(() => {
    handleDeleteAll();
    setComboState("instruments");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset combo instruments on market change
  }, [market]);

  // Ensure the combo active instrument ID is valid and set it accordingly
  useEffect(() => {
    const comboOptionInstrumentsLength = comboOptionInstruments.length;

    // enter addition state if there are no combo option instruments
    if (comboOptionInstrumentsLength === 0) {
      setComboActiveInstrumentId("");
      return;
    }

    // if maximum number of combo option instruments is reached and no active instrument is set, set the last one as active
    if (
      comboOptionInstrumentsLength === maxComboOptionInstruments &&
      !comboActiveInstrumentId
    ) {
      setComboActiveInstrumentId(
        comboOptionInstruments[comboOptionInstrumentsLength - 1].id,
      );
      return;
    }

    // if there is an active instrument, ensure it exists in the combo option instruments
    if (
      comboActiveInstrumentId &&
      comboOptionInstrumentsLength > 0 &&
      !comboOptionInstruments.find(
        (instrument) => instrument.id === comboActiveInstrumentId,
      )
    ) {
      setComboActiveInstrumentId(comboOptionInstruments.at(-1)?.id ?? "");
    }
  }, [
    comboOptionInstruments,
    comboActiveInstrumentId,
    setComboActiveInstrumentId,
  ]);

  return null;
};
